"use client";

import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import ProductPlaceholder from "./ProductPlaceholder";
import { useCart } from "../CartContext";
import { STORE_ENABLED, NUBIA_URL } from "@/lib/store-config";

export interface ProductCardData {
  code: string;             // Display code, e.g. "BP · 23"
  sku: string;              // Numeric SKU, e.g. "1023"
  name: string;
  subtitle: string;
  price: number;
  priceDisplay: string;     // Already-formatted, e.g. "€42"
  priceEUR: number;
  shortName: string;
  href: string;
  tone?: "light" | "dark";
  tag?: string;             // BESTSELLER / NEW / CLINICAL / etc
  image?: string;
  volume?: string;
  /** Localized "+ ADD" label. */
  addLabel?: string;
}

interface Props {
  product: ProductCardData;
}

export default function ProductCard({ product }: Props) {
  const [hover, setHover] = useState(false);
  const { addItem } = useCart();

  const onAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      sku: product.sku,
      name: product.name,
      shortName: product.shortName,
      priceEUR: product.priceEUR,
      image: product.image,
    });
  };

  return (
    <article
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        padding: 20,
        borderRight: "1px solid var(--bp-rule)",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <span className="bp-mono-sm">{product.code}</span>
        {product.tag && (
          <span className="bp-mono-sm" style={{ color: "var(--bp-ink)" }}>
            {product.tag}
          </span>
        )}
      </div>

      <Link
        href={product.href}
        style={{
          position: "relative",
          padding: "20px 12px",
          background: "var(--bp-bg-2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          aspectRatio: "1/1.1",
          overflow: "hidden",
        }}
      >
        {product.image ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              transform: hover ? "scale(1.04)" : "scale(1)",
              transition: "transform 600ms cubic-bezier(.2,.7,.2,1)",
            }}
          >
            <Image
              src={product.image}
              alt={product.name}
              fill
              sizes="(max-width:768px) 100vw, 33vw"
              style={{ objectFit: "cover" }}
            />
          </div>
        ) : (
          <ProductPlaceholder label={product.code} />
        )}

        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 2 }}>
          <span
            className="bp-mono-sm"
            style={{
              position: "absolute",
              top: 8,
              left: 8,
              color: product.image ? "white" : undefined,
              mixBlendMode: product.image ? "difference" : undefined,
            }}
          >
            SKU·{product.sku}
          </span>
          {product.volume && (
            <span
              className="bp-mono-sm"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                color: product.image ? "white" : undefined,
                mixBlendMode: product.image ? "difference" : undefined,
              }}
            >
              {product.volume}
            </span>
          )}
        </div>

        {STORE_ENABLED && (
          <button
            onClick={onAdd}
            aria-label={`Add ${product.name} to bag`}
            style={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 12,
              padding: 12,
              background: "var(--bp-ink)",
              color: "var(--bp-bg)",
              fontFamily: "var(--bp-mono)",
              fontSize: 11,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              border: 0,
              cursor: "pointer",
              opacity: hover ? 1 : 0,
              transform: hover ? "translateY(0)" : "translateY(8px)",
              transition: "all 280ms cubic-bezier(.2,.7,.2,1)",
              zIndex: 3,
            }}
          >
            {product.addLabel ?? "+ ADD"} — {product.priceDisplay}
          </button>
        )}
      </Link>

      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, alignItems: "baseline" }}>
        <Link href={product.href} style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--bp-serif)",
              fontSize: 22,
              fontWeight: 300,
              letterSpacing: "-0.01em",
              lineHeight: 1.1,
              textWrap: "balance" as never,
              color: "var(--bp-ink)",
            }}
          >
            {product.name}
          </div>
          <div style={{ color: "var(--bp-ink-3)", fontSize: 13, marginTop: 4 }}>{product.subtitle}</div>
        </Link>
        <div style={{ fontFamily: "var(--bp-mono)", fontSize: 13, whiteSpace: "nowrap" }}>
          {STORE_ENABLED ? (
            product.priceDisplay
          ) : (
            <a
              href={NUBIA_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--bp-ink-3)", letterSpacing: "0.08em", textTransform: "uppercase" }}
            >
              Nubia ↗
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
